import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'leaflet/dist/leaflet.css';
import { Country } from 'country-state-city';
import * as dashboardApi from '../../../api/dashboardApi';
import * as adminApi from '../../../api/adminApi';
import { GlobalMap } from '../../dashboard/DashboardPage';
import styles from './AdminJurisdictionsTab.module.css';
import metricsStyles from './statistics/MetricsCards.module.css';
import { CountrySelect } from '../../../components/CountrySelect';
import { useAuth } from '../../../context/AuthContext';
import { Folder, Globe } from 'lucide-react';
import { Eyebrow, PageHeading } from '../../../design-system';

const SUPRANATIONALS_FULL: Record<string, string> = {
  EU: 'European Union',
  UN: 'United Nations',
  OECD: 'Organisation for Economic Co-operation and Development',
  GCC: 'Gulf Cooperation Council',
  ASEAN: 'Association of Southeast Asian Nations',
  APEC: 'Asia-Pacific Economic Cooperation',
  AU: 'African Union',
};
const SUPRA_CODES = new Set(Object.keys(SUPRANATIONALS_FULL));

interface AdminJurisdictionsTabProps {
  totalJurisdictions?: number;
  preloadedJurisdictionStats?: dashboardApi.JurisdictionStats[];
}

const AdminJurisdictionsTab: React.FC<AdminJurisdictionsTabProps> = ({
  totalJurisdictions = 0,
  preloadedJurisdictionStats,
}) => {
  const { token } = useAuth();
  const [selectedCountry, setSelectedCountry] = useState('');
  const [jurisdictions, setJurisdictions] = useState<dashboardApi.Jurisdiction[]>([]);
  const [jurisdictionStats, setJurisdictionStats] = useState<dashboardApi.JurisdictionStats[]>(preloadedJurisdictionStats ?? []);
  const [adminByCountry, setAdminByCountry] = useState<Record<string, number>>({});
  const [totalPolicies, setTotalPolicies] = useState(0);
  const [allCountriesCovered, setAllCountriesCovered] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [countryRefs, setCountryRefs] = useState<dashboardApi.CountryRef[]>([]);
  // levelFilter removed — list view no longer supports level filtering
  const [viewMode, setViewMode] = useState<'map' | 'list'>('map');
  const [currentPage, setCurrentPage] = useState(1);

  const ITEMS_PER_PAGE = 8; // 4 cards per row × 2 rows
  const isMountedRef = useRef(false);
  const selectedCountryRef = useRef(selectedCountry);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 480);

  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 480px)');
    if (!mq) return;
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const currentToken = window.localStorage.getItem('policyora_token');
      const adminPromise = currentToken
        ? adminApi.getAdminJurisdictions(currentToken)
        : Promise.resolve({ stats: [], by_country: {} });

      const [adminStatsData, totalData, jurisdictionsData] = await Promise.all([
        adminPromise,
        dashboardApi.getTotalRegulations(),
        dashboardApi.getJurisdictions({ limit: 400 }),
      ]);

      setJurisdictions(jurisdictionsData || []);
      setJurisdictionStats((adminStatsData && (adminStatsData as any).stats) || []);
      setTotalPolicies((totalData && (totalData as any).total) || 0);
      if ((adminStatsData as any)?.by_country) {
        const byCountry = (adminStatsData as any).by_country as Record<string, number>;
        setAdminByCountry(byCountry);
        const validCount = Object.keys(byCountry)
          .filter(k => !SUPRA_CODES.has(k) && !!Country.getCountryByCode(k))
          .length;
        setAllCountriesCovered(validCount);
      } else {
        setAllCountriesCovered((jurisdictionsData || []).filter((j: any) => (j.type || '').toLowerCase().includes('country')).length);
      }
      setError(null);
    } catch (err) {
      setError((err as Error).message || 'Failed to load jurisdictions');
    } finally {
      setLoading(false);
    }

  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handler = () => {
      setSelectedCountry('');
      setSearchQuery('');
      setViewMode('map');
      setCurrentPage(1);
    };
    window.addEventListener('admin:reset-filters', handler as EventListener);
    return () => window.removeEventListener('admin:reset-filters', handler as EventListener);
  }, []);

  useEffect(() => {
    selectedCountryRef.current = selectedCountry;
  }, [selectedCountry]);

  // Country reference list to translate a map click (ISO-2) → country UUID.
  useEffect(() => {
    dashboardApi.getCountries().then(setCountryRefs).catch(() => {});
  }, []);

  useEffect(() => {
    // Skip on initial mount — loadData() already handles the first fetch
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    // When country changes, reload jurisdictions for that country
    const loadCountryJurisdictions = async () => {
      setLoading(true);
      try {
        const currentToken = window.localStorage.getItem('policyora_token');
        const adminPromise = currentToken
          ? adminApi.getAdminJurisdictions(currentToken, selectedCountry || undefined)
          : Promise.resolve({ stats: [], by_country: {} });

        const [jurisdictionsData, adminStatsData] = await Promise.all([
          dashboardApi.getJurisdictions({ country_id: selectedCountry || undefined, limit: 400 }),
          adminPromise,
        ]);
        setJurisdictions(jurisdictionsData);
        setJurisdictionStats((adminStatsData && (adminStatsData as any).stats) || []);
        setError(null);
      } catch (err) {
        setError((err as Error).message || 'Failed to load jurisdictions');
      } finally {
        setLoading(false);
      }
    };
    loadCountryJurisdictions();
  }, [selectedCountry, token]);


  // Filter jurisdictions based on search query
  const filteredJurisdictions = jurisdictions.filter(jurisdiction => {
    return !searchQuery ||
      jurisdiction.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      jurisdiction.code.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const supranationalEntries = useMemo(() => {
    const counts: Record<string, number> = {};
    jurisdictionStats.forEach(s => {
      const prefix = (s.jurisdiction_code || '').split('-')[0].toUpperCase();
      if (SUPRA_CODES.has(prefix)) counts[prefix] = (counts[prefix] || 0) + s.regulation_count;
    });
    return Object.entries(SUPRANATIONALS_FULL)
      .filter(([code]) => (counts[code] ?? 0) > 0)
      .map(([code, name]) => ({ code, name, count: counts[code] }));
  }, [jurisdictionStats]);

  // Get KPI data
  const kpis = {
    total: totalJurisdictions || jurisdictions.length,
    activePolicies: totalPolicies,
    countriesCovered: allCountriesCovered ?? jurisdictions.filter(j => (j.type || '').toLowerCase().includes('country')).length,
  };

  // Get regional data for chart
  // List of all continents (ensure all are shown)
  const allContinents = [
    'Africa',
    'Antarctica',
    'Asia',
    'Europe',
    'North America',
    'Oceania',
    'South America',
  ];
  // Aggregate jurisdiction and regulation count by continent
  const continentStats = allContinents.map((continent) => {
    const juris = jurisdictions.filter(j => (j.continent_name || 'Other') === continent);
    const jurisIds = new Set(juris.map(j => j.id));
    const regulations = jurisdictionStats
      .filter(s => jurisIds.has(s.jurisdiction_id))
      .reduce((sum, s) => sum + (s.regulation_count || 0), 0);
    return {
      continent,
      jurisdictions: juris.length,
      regulations,
    };
  });

  // Get jurisdiction levels distribution
  const levelsData = jurisdictions.reduce((acc, jurisdiction) => {
    const level = jurisdiction.type || 'Other';
    if (!acc[level]) acc[level] = 0;
    acc[level]++;
    return acc;
  }, {} as Record<string, number>);

  // Recently updated jurisdictions — the five whose newest regulation is most recent. The list of
  // jurisdictions itself rarely changes (the last batch was added in April 2026), so "recently
  // added" showed months-old rows with a "New" badge; what moves is the regulations under them.
  // "New" is shown only when the jurisdiction itself was added within the last 30 days.
  const lastRegulationDate = (id: string | number) =>
    jurisdictionStats.find((st) => String(st.jurisdiction_id) === String(id))?.most_recent_regulation_date || null;
  const recentlyUpdatedJurisdictions = jurisdictions
    .map((j) => ({ j, when: lastRegulationDate(j.id) }))
    .filter((x): x is { j: typeof x.j; when: string } => !!x.when)
    .sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime())
    .slice(0, 5);
  const isNewJurisdiction = (createdAt?: string | null) =>
    !!createdAt && Date.now() - new Date(createdAt).getTime() < 30 * 24 * 60 * 60 * 1000;

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div className={styles.root}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <Eyebrow tone="default" dot>JURISDICTIONS</Eyebrow>
          <PageHeading
            accentWord="management"
            accentTone="orange"
            size="h2"
            align="left"
            level="h2"
          >
            Jurisdiction management
          </PageHeading>
        </div>
        <button
          onClick={() => setViewMode(viewMode === 'map' ? 'list' : 'map')}
          className={viewMode === 'map' ? styles.viewToggleActive : styles.viewToggle}
        >
          {viewMode === 'map' ? 'Map View' : 'List View'}
        </button>
      </div>

      {/* Search and Filters - Hidden in Map View */}
      {viewMode === 'list' && (
        <div className={styles.listToolbar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Country</label>
            <div className={styles.countrySelectWrapper}>
              <CountrySelect
                token={token || ''}
                value={selectedCountry}
                onChange={setSelectedCountry}
                label=""
                className={styles.countrySelect}
                style={{ minWidth: '100%' }}
              />
            </div>
          </div>
          <input
            type="search"
            placeholder="Search jurisdictions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
          {/* level filter removed */}
        </div>
      )}

      {/* KPI Cards */}
      <div className={styles.kpiGrid}>
        <div className={`${metricsStyles.metricCard} ${metricsStyles.metric_blue}`}>
          <span className={metricsStyles.metricIconBadge}>J</span>
          <div className={metricsStyles.metricContent}>
            <div className={metricsStyles.metricLabel}>Total Jurisdictions</div>
            <div className={metricsStyles.metricValue}>{kpis.total}</div>
            <div className={metricsStyles.metricMeta}>Included in system</div>
          </div>
        </div>

        <div className={`${metricsStyles.metricCard} ${metricsStyles.metric_green}`}>
          <span className={metricsStyles.metricIconBadge}><Folder size={20} aria-hidden /></span>
          <div className={metricsStyles.metricContent}>
            <div className={metricsStyles.metricLabel}>Active Policies</div>
            <div className={metricsStyles.metricValue}>{kpis.activePolicies.toLocaleString()}</div>
            <div className={metricsStyles.metricMeta}>Current active regulations</div>
          </div>
        </div>

        <div className={`${metricsStyles.metricCard} ${metricsStyles.metric_gold}`}>
          <span className={metricsStyles.metricIconBadge}><Globe size={20} aria-hidden /></span>
          <div className={metricsStyles.metricContent}>
            <div className={metricsStyles.metricLabel}>Countries Covered</div>
            <div className={metricsStyles.metricValue}>{kpis.countriesCovered}</div>
            <div className={metricsStyles.metricMeta}>Distinct countries</div>
          </div>
        </div>

        <div className={`${metricsStyles.metricCard} ${metricsStyles.metric_pink}`}>
          <span className={metricsStyles.metricIconBadge}><Globe size={20} aria-hidden /></span>
          <div className={metricsStyles.metricContent}>
            <div className={metricsStyles.metricLabel}>Supranationals</div>
            <div className={metricsStyles.metricValue}>{supranationalEntries.length}</div>
            <div className={metricsStyles.metricMeta} title={supranationalEntries.map(s => `${s.name} (${s.count})`).join('\n')}>
              {supranationalEntries.length > 0
                ? supranationalEntries.map(s => s.code).join(' · ')
                : 'None'}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={viewMode === 'map' ? styles.contentMap : styles.contentList}>
        {viewMode === 'list' ? (
          <>
            {/* Jurisdiction Cards Grid - List View */}
            <div className={styles.cardsGrid}>
              {filteredJurisdictions
                .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
            .map(jurisdiction => {
              const stats = jurisdictionStats.find(s => s.jurisdiction_id === jurisdiction.id);
              return (
                <div key={jurisdiction.id} className={styles.card}>
                    <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '0.75rem',
                    gap: '0.75rem'
                  }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      background: 'var(--admin-accent, #6172f3)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--admin-button-text, white)',
                      fontWeight: 700,
                      fontSize: '0.85rem'
                    }}>
                      {jurisdiction.code?.substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 700 }}>
                        {jurisdiction.name}
                      </h3>
                      <p style={{ margin: '0.2rem 0 0', fontSize: '0.7rem', color: 'var(--admin-text-muted, #667085)' }}>
                        {jurisdiction.type || 'Unknown'}
                      </p>
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid #eaecf0', paddingTop: '0.75rem' }}>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <p style={{ margin: '0 0 0.2rem 0', fontSize: '0.7rem', color: '#667085', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>Policies Tracked</span>
                        <span title="Counts reflect how artifacts are mapped in ingestion_jurisdictions. Some sources assign artifacts to a parent country, so constituent jurisdictions may show 0 until artifacts are remapped or ingested with the correct jurisdiction." style={{ cursor: 'help', fontSize: '0.75rem', color: 'var(--ds-text-muted)' }}>ℹ️</span>
                      </p>
                      <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }} title={stats ? `${stats.regulation_count.toLocaleString()} policies (per ingestion mapping)` : undefined}>
                        {stats?.regulation_count.toLocaleString() || 0}
                      </p>
                    </div>
                    <div>
                      <p style={{ margin: '0 0 0.2rem 0', fontSize: '0.7rem', color: 'var(--admin-text-muted, #667085)' }}>
                        Priority Level
                      </p>
                      <p style={{
                        margin: 0,
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: (stats?.regulation_count || 0) > 1000 ? 'var(--admin-note, #f04438)' : 'var(--admin-accent, #444ce7)'
                      }}>
                        {(stats?.regulation_count || 0) > 1000 ? 'Very High' : 'High'}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
            </div>

            {/* Pagination */}
            {filteredJurisdictions.length > ITEMS_PER_PAGE && (
              <div className={styles.pagination}>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={styles.pageBtn}
                >
                  Previous
                </button>
                <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                  Page {currentPage} of {Math.max(1, Math.ceil(filteredJurisdictions.length / ITEMS_PER_PAGE))}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.max(1, Math.ceil(filteredJurisdictions.length / ITEMS_PER_PAGE))))}
                  disabled={currentPage >= Math.max(1, Math.ceil(filteredJurisdictions.length / ITEMS_PER_PAGE))}
                  className={styles.pageBtn}
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Global Map View */}
            <div className={styles.mapWrapper}>
              <GlobalMap
                stats={jurisdictionStats}
                countryStats={adminByCountry}
                isLoading={loading}
                selectedLocation={selectedLocation}
                onCountryClick={(code) => {
                  // Drill down: map ISO-2 → country UUID, then switch to the
                  // list view filtered to that country's jurisdictions.
                  const match = countryRefs.find(c => (c.iso2 || '').toUpperCase() === code.toUpperCase());
                  if (match) {
                    setSelectedCountry(match.id);
                    setViewMode('list');
                  }
                }}
              />
            </div>
          </>
        )}

        {/* Charts Section */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
          gap: '1.5rem',
          marginTop: 0
        }}>
          {/* Continent-wise Regulation Count Bar Chart (all continents, wide bars, show both counts) */}
          <div className={styles.widgetCard}>
            <h3 className={styles.widgetTitle}>
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}>
                <span>Continent-wise Count</span>
                <span style={{
                  fontWeight: 600,
                  fontSize: isMobile ? '0.78rem' : '1.05rem',
                  color: '#039855',
                  background: '#ecfdf3',
                  borderRadius: 6,
                  padding: '2px 8px',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                }}>
                  Jurisdictions: {jurisdictions.length.toLocaleString()}
                </span>
                <span style={{
                  fontWeight: 600,
                  fontSize: isMobile ? '0.78rem' : '1.05rem',
                  color: '#039855',
                  background: '#ecfdf3',
                  borderRadius: 6,
                  padding: '2px 8px',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                }}>
                  Regulations: {jurisdictionStats.reduce((sum, s) => sum + (s.regulation_count || 0), 0).toLocaleString()}
                </span>
                <button
                  type="button"
                  onClick={() => { const t = window.localStorage.getItem('policyora_token'); if (t) adminApi.downloadJurisdictionsCsv(t, selectedCountry || undefined).catch(() => {}); }}
                  style={{ marginLeft: 'auto', fontSize: '0.8rem', fontWeight: 600, padding: '4px 10px', borderRadius: 6,
                           border: '1px solid var(--admin-border, var(--ds-border))', background: 'var(--admin-surface, var(--ds-surface))', color: 'var(--ds-text)', cursor: 'pointer' }}
                >
                  Download CSV
                </button>
              </span>
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
              {continentStats.map((stat, idx) => {
                const barColors = ['#444ce7', '#12b76a', '#444ce7', '#f04438', '#6172f3', '#f97066', '#12b76a'];
                const maxReg = Math.max(...continentStats.map(s => s.regulations), 1);
                return (
                  <div key={stat.continent} style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                    <span className={styles.continentLabel} style={{ width: isMobile ? 72 : 140, fontSize: isMobile ? '0.78rem' : '1rem', fontWeight: 500, flexShrink: 0 }}>{stat.continent}</span>
                    <div style={{
                      flex: 1,
                      margin: isMobile ? '0 8px' : '0 16px',
                      background: 'var(--ds-surface-2)',
                      height: '22px',
                      borderRadius: 0,
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      <div style={{
                        background: barColors[idx % barColors.length],
                        height: '100%',
                        width: stat.regulations > 0 ? `${Math.max(10, (stat.regulations / maxReg) * 100)}%` : '10%',
                        minWidth: stat.regulations > 0 ? 36 : 0,
                        borderRadius: 0,
                        transition: 'width 0.5s',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                      }} />
                    </div>
                    <span className={styles.continentCount} style={{ fontSize: isMobile ? '0.85rem' : '1.05rem', fontWeight: 700, marginLeft: 8, minWidth: isMobile ? 36 : 60, textAlign: 'right' }}>{stat.regulations.toLocaleString()}</span>
                    {!isMobile && <span className={styles.continentJurCount} style={{ fontSize: '0.95rem', color: '#667085', marginLeft: 8, minWidth: 80, textAlign: 'left' }}>({stat.jurisdictions} jurisdictions)</span>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Next row: Jurisdiction Levels Distribution and Recent Updates visually separated */}
          <div style={{ width: '100%', marginTop: 36 }}>
            <div className={styles.chartsRow} style={{ display: 'flex', gap: '1.5rem', width: '100%' }}>
              <div className={styles.widgetCard} style={{ flex: 1 }}>
                <h3 className={styles.widgetTitle}>Jurisdiction Levels Distribution</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {Object.entries(levelsData).map(([level, count]) => (
                    <div key={level} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: level === 'Federal' ? '#6172f3' : level === 'State' ? '#12b76a' : level === 'Local' ? '#444ce7' : '#f04438' }} />
                        <span style={{ fontSize: '0.85rem' }}>{level}</span>
                      </div>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.widgetCard} style={{ flex: 1 }}>
                <h3 className={styles.widgetTitle}>Recently Updated Jurisdictions</h3>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.78rem', color: 'var(--admin-text-muted)' }}>By the date of their newest regulation</p>
                <div className={styles.widgetList}>
                  {recentlyUpdatedJurisdictions.length === 0 ? (
                    <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--admin-text-muted)' }}>No regulation activity recorded yet</p>
                  ) : recentlyUpdatedJurisdictions.map(({ j, when }) => (
                    <div key={j.id} className={styles.widgetListItem}>
                      <div>
                        <p className={styles.title}>{j.name}</p>
                        <p className={styles.meta}>{j.type || 'Jurisdiction'}{j.country_name ? ` · ${j.country_name}` : ''}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--admin-text-muted)' }}>Latest regulation {formatDate(when)}</p>
                        {isNewJurisdiction(j.created_at) && <span className={`${styles.statusBadge} ${styles.statusActive}`}>New</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminJurisdictionsTab;
